// src/component/admin/user/CustomerDetailsModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  CloseIcon,
  PhoneIcon,
  EmailIcon,
  ProductCatalogIcon,
  GiftIcon,
  LocationIcon,
  FileIcon,
  CalendarIcon,
  AccountIcon
} from '../../../public/icons';
import adminUserService from '../../../services/admin/admin-usersService';
import { useToast } from '../../../context/ToastContext';

const CustomerDetailsModal = ({ isOpen, onClose, customer, onStatusChange,  onViewOrder  }) => {
  const [activeTab, setActiveTab] = useState('orders');
  const [internalNote, setInternalNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [futureGifts, setFutureGifts] = useState([]);
  const [savingNote, setSavingNote] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const { success, error: showError } = useToast();

  /* ────────────── Fetch helpers ────────────── */

  const fetchCustomerDetails = useCallback(async () => {
    if (!customer?._id) return;
    try {
      setLoading(true);
      const response = await adminUserService.getUserById(customer._id);

      const fetchedUser = response?.data?.user || response?.user || null;

      if (response?.success && fetchedUser) {
        setCustomerData(fetchedUser);
        const notes = fetchedUser.internalNotes || [];
        setInternalNote(notes.length > 0 ? notes[notes.length - 1].note || '' : '');
      } else {
        setCustomerData((prev) => prev || customer);
        if (!response?.success) {
          showError(response?.message || 'Failed to fetch customer details');
        }
      }
    } catch (error) {
      console.error('Error fetching customer details:', error);
      showError('Failed to load customer details');
      setCustomerData((prev) => prev || customer);
    } finally {
      setLoading(false);
    }
  }, [customer, showError]);

  const fetchCustomerOrders = useCallback(async () => {
    if (!customer?._id) return;
    try {
      const response = await adminUserService.getUserOrders(customer._id, {
        limit: 20
      });
      if (response.success) {
        setOrders(response.data?.orders || []);
      }
    } catch (error) {
      console.error('Error fetching customer orders:', error);
    }
  }, [customer?._id]);

  const fetchCustomerFutureGifts = useCallback(async () => {
    if (!customer?._id) return;
    try {
      const response = await adminUserService.getUserFutureGifts(customer._id, {
        limit: 20
      });
      if (response.success) {
        setFutureGifts(response.data?.futureGifts || []);
      }
    } catch (error) {
      console.error('Error fetching future gifts:', error);
    }
  }, [customer?._id]);

  useEffect(() => {
    if (!isOpen || !customer?._id) return;
    setCustomerData(null);
    setOrders([]);
    setFutureGifts([]);
    setInternalNote('');
    fetchCustomerDetails();
    fetchCustomerOrders();
    fetchCustomerFutureGifts();
  }, [
    isOpen,
    customer?._id,
    fetchCustomerDetails,
    fetchCustomerOrders,
    fetchCustomerFutureGifts
  ]);

  /* ────────────── Actions ────────────── */

  const handleSaveNote = async () => {
    if (!customer?._id || !internalNote.trim()) return;
    try {
      setSavingNote(true);
      const response = await adminUserService.addUserNote(
        customer._id,
        internalNote.trim()
      );
      if (response.success) {
        success('Note saved successfully');
        await fetchCustomerDetails();
      } else {
        showError(response.message || 'Failed to save note');
      }
    } catch (error) {
      console.error('Error saving note:', error);
      showError('Failed to save note. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!customer?._id) return;
    try {
      setRefreshing(true);
      const response = await adminUserService.updateUserStatus(
        customer._id,
        newStatus
      );
      if (response.success) {
        success(`Customer status updated to ${newStatus}`);
        await fetchCustomerDetails();
        if (onStatusChange) onStatusChange();
      } else {
        showError(response.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showError('Failed to update status. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  /* ────────────── Formatters ────────────── */

  const getInitials = (name) => {
    if (!name) return 'CU';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getOrderStatusBadge = (status) => {
    const map = {
      pending: 'bg-[#FFF4D6] text-[#D8A100]',
      processing: 'bg-[#E7ECF4] text-[#2F578F]',
      content_review: 'bg-[#E7ECF4] text-[#2F578F]',
      in_production: 'bg-[#F4F2E7] text-[#8F7F2F]',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-[#E7F4ED] text-[#2F8F5B]',
      cancelled: 'bg-[#F4E7E7] text-[#A82121]',
      refunded: 'bg-gray-100 text-gray-800'
    };
    const label = String(status || '').replace(/_/g, ' ');
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap ${
          map[status] || 'bg-gray-100 text-gray-800'
        }`}
      >
        {label}
      </span>
    );
  };

  const getCustomerStatusBadge = (status) => {
    const map = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      suspended: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
          map[status] || 'bg-gray-100 text-gray-800'
        }`}
      >
        {status || 'unknown'}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      maximumFractionDigits: 0
    }).format(Number(amount) || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateDaysUntil = (deliveryDate) => {
    if (!deliveryDate) return 'No date set';
    const today = new Date();
    const delivery = new Date(deliveryDate);
    const diffDays = Math.ceil((delivery - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'Past due';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `${diffDays} days until delivery`;
  };

  /* ────────────── Early returns ────────────── */
  if (!isOpen || !customer) return null;

  if (loading && !customerData) {
    return (
      <>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100]" />
        <div className="fixed right-0 top-0 h-full w-full md:w-[70%] lg:w-[60%] xl:w-[50%] bg-white z-[999] overflow-y-auto">
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D2A146] mx-auto" />
              <p className="mt-4 text-gray-600">Loading customer details…</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const data = customerData || {
    ...customer,
    firstName: customer.firstName || '',
    lastName: customer.lastName || '',
    email: customer.email || '',
    phone: customer.phone || '',
    status: customer.status || 'active',
    totalOrders: customer.totalOrders || 0,
    totalSpent: customer.totalSpent || 0,
    addresses: [],
    internalNotes: []
  };

  const customerName =
    `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Customer';
  const customerSince = data.createdAt
    ? new Date(data.createdAt).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      })
    : 'Unknown';

  const avgOrderValue =
    data.totalOrders > 0 ? Number(data.totalSpent || 0) / data.totalOrders : 0;

  const futureGiftsCount = data.futureGiftsCount ?? futureGifts.length;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[100]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed right-0 top-0 h-full w-full md:w-[90%] lg:w-[90%] xl:w-[60%] bg-white z-[999] overflow-hidden">
        <div className="h-full flex flex-col text-left">
          {/* ─────────── Header ─────────── */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center space-x-4 min-w-0">
              <div className="h-16 w-16 bg-[#4F7FDB] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-lg font-medium">
                  {getInitials(customerName)}
                </span>
              </div>
              <div className="min-w-0">
                <h2 className="text-2xl font-bold text-[#0E1628] truncate">
                  {customerName}
                </h2>
                <div className="flex flex-wrap items-center gap-4 mt-1">
                  <div className="flex items-center text-[#8A9099] min-w-0">
                    <EmailIcon className="w-5 h-5 mr-1 flex-shrink-0" color="#8A9099" />
                    <span className="text-sm truncate">{data.email || '—'}</span>
                  </div>
                  {data.phone && (
                    <div className="flex items-center text-[#8A9099]">
                      <PhoneIcon className="w-4 h-4 mr-1 flex-shrink-0" color="#8A9099" />
                      <span className="text-sm">{data.phone}</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  {getCustomerStatusBadge(data.status)}
                  <span className="text-xs text-[#0E1628] flex items-center">
                    <CalendarIcon className="w-3 h-3 mr-1" />
                    Customer since: {customerSince}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0"
            >
              <CloseIcon className="w-6 h-6 text-[#8A9099]" />
            </button>
          </div>

          {/* ─────────── Stats Row ─────────── */}
          <div className="bg-gray-50 flex flex-wrap justify-around gap-4 px-6 py-5 flex-shrink-0">
            <div className="text-center">
              <div className="text-sm text-[#8A9099]">Total Orders</div>
              <div className="text-2xl font-medium text-[#0E1628] mt-1.5">
                {data.totalOrders || 0}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-[#8A9099]">Total Spent</div>
              <div className="text-2xl font-medium text-[#0E1628] mt-1.5">
                {formatCurrency(data.totalSpent || 0)}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-[#8A9099]">Avg. Order Value</div>
              <div className="text-2xl font-medium text-[#0E1628] mt-1.5">
                {formatCurrency(avgOrderValue)}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-[#8A9099]">Future Gifts</div>
              <div className="text-2xl font-medium text-[#0E1628] mt-1.5">
                {futureGiftsCount}
              </div>
            </div>
          </div>

          {/* ─────────── Main content: scrollable ─────────── */}
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col lg:flex-row">
              {/* LEFT SIDE — orders + gifts */}
              <div className="flex-1 min-w-0 p-6 space-y-5">
                {/* Purchase History */}
                <div className="border-2 border-[#E4E4E4] overflow-hidden">
                  <div className="flex items-center justify-between py-5 px-4 border-b-2 border-[#E4E4E4]">
                    <h3 className="text-lg font-medium text-[#0E1628] flex items-center gap-2">
                      <ProductCatalogIcon color="#55000C" />
                      Purchase History
                    </h3>
                    <span className="text-sm text-[#8A9099] whitespace-nowrap">
                      {orders.length} Orders
                    </span>
                  </div>

                  {orders.length > 0 ? (
                    <div className="divide-y divide-[#E4E4E4]">
                      {orders.map((order, index) => {
                        const itemTitles = (order.items || [])
                          .map(
                            (it) =>
                              it.title ||
                              it.productId?.name ||
                              it.productId ||
                              'Item'
                          )
                          .filter(Boolean);

                        const itemSummary =
                          itemTitles.length > 1
                            ? `${itemTitles[0]} +${itemTitles.length - 1} more`
                            : itemTitles[0] || 'No items';

                        return (
                          <div
                            key={order._id || index}
                            className="px-4 py-4 min-w-0"
                          >
                            <div className="flex items-start justify-between gap-4 min-w-0">
                              {/* Left: order meta — allow shrinking */}
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-medium text-[#0E1628] text-base">
                                    {order.orderNumber ||
                                      `ORD-${order._id?.slice(-6)}`}
                                  </span>
                                  {getOrderStatusBadge(order.orderStatus)}
                                </div>
                                <p
                                  className="text-[#8A9099] text-sm mt-1 truncate"
                                  title={itemTitles.join(', ')}
                                >
                                  {itemSummary}
                                </p>
                                <p className="text-[#8A9099] text-xs mt-1">
                                  {formatDate(order.createdAt)}
                                </p>
                              </div>

                              {/* Right: price + button */}
                              <div className="text-right flex-shrink-0">
                                <p className="font-medium text-base text-[#0E1628] whitespace-nowrap">
                                  {formatCurrency(order.total || 0)}
                                </p>
                                <button
  onClick={() => {
    if (onViewOrder) {
      // pass the order's Mongo _id to the parent
      onViewOrder(order._id);
    }
  }}
  disabled={!order._id}
  className="text-[#55000C] text-sm hover:underline whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
>
  View Order
</button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="px-4 py-8 text-center">
                      <p className="text-[#8A9099]">
                        No orders found for this customer
                      </p>
                    </div>
                  )}
                </div>

                {/* Scheduled Future Gifts */}
                <div className="border-2 border-[#E4E4E4] overflow-hidden">
                  <div className="flex items-center justify-between py-5 px-4 border-b-2 border-[#E4E4E4]">
                    <h3 className="text-lg font-medium text-[#0E1628] flex items-center gap-2">
                      <GiftIcon color="#55000C" />
                      Scheduled Future Gifts
                    </h3>
                    <span className="text-sm text-gray-500 whitespace-nowrap">
                      {futureGifts.length} Scheduled
                    </span>
                  </div>

                  {futureGifts.length > 0 ? (
                    <div className="divide-y divide-[#E4E4E4]">
                      {futureGifts.map((gift, index) => (
                        <div
                          key={gift._id || index}
                          className="px-4 py-4 min-w-0"
                        >
                          <div className="flex items-start justify-between gap-4 min-w-0">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <span className="font-medium text-[#0E1628]">
                                  {formatDate(gift.deliveryDate)}
                                </span>
                                {getOrderStatusBadge(gift.status)}
                              </div>
                              <p className="text-[#8A9099] text-sm mt-1 truncate">
                                {gift.occasion ||
                                  gift.title ||
                                  'Special Occasion'}
                              </p>
                              <p className="text-[#8A9099] text-xs mt-1">
                                {calculateDaysUntil(gift.deliveryDate)}
                              </p>
                              {gift.recipientId && (
                                <p className="text-[#8A9099] text-xs mt-1 truncate">
                                  Recipient:{' '}
                                  {gift.recipientId.firstName || ''}{' '}
                                  {gift.recipientId.lastName || ''}
                                </p>
                              )}
                            </div>
                            
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-8 text-center">
                      <p className="text-[#8A9099]">
                        No future gifts scheduled
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT SIDE — addresses, actions, notes */}
              <div className="w-full lg:w-96 lg:flex-shrink-0 border-t lg:border-t-0 lg:border-l border-[#E4E4E4] p-6 space-y-5">
                {/* Shipping Addresses */}
                <div className="bg-[#F9FAFB] p-5">
                  <h3 className="text-lg font-medium text-[#0E1628] flex items-center gap-2 mb-4">
                    <LocationIcon className="w-5 h-5" color="#55000C" />
                    Shipping Addresses
                  </h3>

                  {data.addresses && data.addresses.length > 0 ? (
                    <div className="space-y-4">
                      {data.addresses.map((addr, index) => (
                        <div
                          key={addr._id || index}
                          className="p-3 bg-white rounded border border-gray-200"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                addr.isDefault
                                  ? 'bg-green-100 text-[#2F8F5B]'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {addr.isDefault
                                ? 'Default'
                                : addr.type || 'Address'}
                            </span>
                          </div>
                          <p className="text-sm text-[#8A9099] leading-relaxed break-words">
                            {addr.fullName}
                            <br />
                            {addr.addressLine1}
                            {addr.addressLine2 && (
                              <>
                                <br />
                                {addr.addressLine2}
                              </>
                            )}
                            <br />
                            {addr.city}, {addr.state} {addr.postalCode}
                            <br />
                            {addr.country}
                          </p>
                          {addr.phone && (
                            <p className="text-sm text-gray-600 mt-2">
                              📞 {addr.phone}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-[#8A9099]">
                        No addresses saved
                      </p>
                    </div>
                  )}
                </div>

            

                {/* Internal Notes */}
                <div className="bg-[#F9FAFB] p-5">
                  <h3 className="text-lg font-medium text-[#0E1628] flex items-center gap-2 mb-4">
                    <FileIcon className="w-5 h-5" color="#55000C" />
                    Internal Notes
                  </h3>

                  <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3">
                    <textarea
                      value={internalNote}
                      onChange={(e) => setInternalNote(e.target.value)}
                      className="w-full h-32 text-sm text-gray-700 border-none resize-none focus:outline-none"
                      placeholder="Add internal notes about this customer…"
                    />
                  </div>

                  <div className="flex justify-between items-center mt-2">
                    <div className="text-xs text-gray-500">
                      {data.internalNotes?.length || 0} notes total
                    </div>
                    <button
                      onClick={handleSaveNote}
                      disabled={!internalNote.trim() || savingNote}
                      className={`px-4 py-2 rounded text-sm font-medium ${
                        internalNote.trim() && !savingNote
                          ? 'bg-[#D2A146] hover:bg-[#C49660] text-white'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {savingNote ? 'Saving…' : 'Save Note'}
                    </button>
                  </div>

                  {data.internalNotes && data.internalNotes.length > 1 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Previous Notes
                      </h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {[...data.internalNotes]
                          .reverse()
                          .slice(1)
                          .map((note, index) => (
                            <div
                              key={note._id || index}
                              className="bg-gray-50 p-3 rounded text-sm"
                            >
                              <p className="text-gray-600 break-words">
                                {note.note}
                              </p>
                              <div className="flex justify-between items-center mt-1 text-xs text-gray-500">
                                <span>{note.adminName || 'Admin'}</span>
                                <span>{formatDate(note.createdAt)}</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomerDetailsModal;